<template>
  <div class="container">
    <span class="resize-loading" v-if="loading">
      <span class="loader"></span>
    </span>

    <div v-if="serviceData.is_active" class="additional-configs">
      <!-- Désactivation du service -->
      <div v-if="!serviceData.is_deactivated" class="mb-3 activate-service">
        <el-button
          class="text-danger"
          type="text"
          @click="toggleServiceActivation(false)"
        >
          <i class="el-icon-turn-off"></i> {{ $t("ELIXIO_CONNECT.DEACTIVATE") }}
          {{ serviceData.name }}</el-button
        >
      </div>
      <div class="form-wrapper full">
        <el-tabs v-model="activeTab" stretch>
          <el-tab-pane
            v-if="additionalConfigFields.length"
            :label="$t('ELIXIO_CONNECT.ELIXIO_ADDITIONNAL_CONFIG_TITLE')"
            name="tab-configuration"
          >
            <module-additional-config-form
              v-model="serviceData.additional_config"
              :fields="additionalConfigFields"
              :disabled="loading"
            />
          </el-tab-pane>
          <el-tab-pane
            v-if="serviceData.is_active && serviceData.batch_synchable"
            label="Batch Sync"
            name="tab-batch-connect"
          >
            <module-data-batch-sync
              :organization="organization"
              :service="service"
            />
          </el-tab-pane>
        </el-tabs>
      </div>
    </div>
    <div v-else class="service-authorisation">
      <!-- Réactivation du service -->
      <div v-if="serviceData.is_deactivated" class="mb-3 activate-service">
        <el-button
          class="text-success"
          type="text"
          @click="toggleServiceActivation(true)"
        >
          <i class="el-icon-open"></i> {{ $t("ELIXIO_CONNECT.REACTIVATE") }}
          {{ serviceData.name }}</el-button
        >
      </div>
      <div v-if="serviceData.auth_mode !== 'oauth2'" class="form-wrapper full">
        <el-tabs
          v-if="credentialsFields.length"
          v-model="activeTabMode"
          :stretch="true"
        >
          <el-tab-pane
            :label="$t('ELIXIO_CONNECT.ELIXIO_CONNECT_TEST_CONFIG')"
            :name="SERVICE_MODE_TEST"
          >
            <module-config-form
              v-model="test_config"
              :serviceType="serviceData.slug"
              :fields="credentialsFields"
              :disabled="loading"
              :title="$t('ELIXIO_CONNECT.ELIXIO_CONNECT_TEST_CONFIG')"
            />
          </el-tab-pane>

          <el-tab-pane
            :label="$t('ELIXIO_CONNECT.ELIXIO_CONNECT_PROD_CONFIG')"
            :name="SERVICE_MODE_PROD"
          >
            <module-config-form
              v-model="prod_config"
              :serviceType="serviceData.slug"
              :fields="credentialsFields"
              :disabled="loading"
              :title="$t('ELIXIO_CONNECT.ELIXIO_CONNECT_PROD_CONFIG')"
            />
          </el-tab-pane>
        </el-tabs>
      </div>

      <module-o-auth2-connect
        v-else
        :organization="organization"
        :service="service"
        @onSubscribed="handleFormSubmited"
      />

      <div class="form-wrapper full" v-if="serviceData.instructions">
        <p v-html="serviceData.instructions"></p>
      </div>
    </div>

    <div
      class="elite-modal-forms"
      v-if="
        !(
          (serviceData.auth_mode == 'oauth2' && !serviceData.is_active) ||
          activeTab !== 'tab-configuration'
        ) &&
        ((serviceData.is_active && additionalConfigFields.length) ||
          (!serviceData.is_active &&
            (additionalConfigFields.length || credentialsFields.length)))
      "
    >
      <button
        type="button"
        class="btn close"
        @click="close"
        :disabled="loading"
      >
        {{ $t("COMMON.CLOSE") }}
      </button>

      <button
        type="submit"
        class="btn edit"
        @click="handleSubmit"
        :disabled="loading"
      >
        <span v-if="loading" class="btn-inner--icon">
          <i class="fas fa-spinner fa-spin"></i>
        </span>
        {{ $t("ELIXIO_CONNECT.ELIXIO_CONNECT_SUBMIT") }}
      </button>
    </div>
  </div>
</template>

<script>
import { cloneDeep } from "lodash";
import { Card, Button, Tabs, TabPane } from "element-ui";
import swal from "sweetalert2";
import ModuleConfigForm from "./ModuleCredentialForm.vue";
import ModuleAdditionalConfigForm from "./ModuleAdditionalConfigForm.vue";
import { SERVICE_MODE_TEST, SERVICE_MODE_PROD } from "../constants/common";
import ModuleOAuth2Connect from "./ModuleOAuth2Connect.vue";
import ModuleDataBatchSync from "./ModuleDataBatchSync.vue";

import { QUERY_ACTIONS_ADD } from "@/constants/common";

export default {
  name: "ModuleCredentialEditor",

  components: {
    [Button.name]: Button,
    [Card.name]: Card,
    [Tabs.name]: Tabs,
    [TabPane.name]: TabPane,
    ModuleConfigForm,
    ModuleAdditionalConfigForm,
    ModuleOAuth2Connect,
    ModuleDataBatchSync,
  },

  props: ["action", "service", "organization"],

  data() {
    const serviceData = cloneDeep(this.service);

    return {
      loading: false,
      subscribed: false,
      SERVICE_MODE_TEST,
      SERVICE_MODE_PROD,
      serviceData,
      activeTabMode: serviceData?.mode || SERVICE_MODE_TEST,
      test_config: serviceData?.test_config || [],
      prod_config: serviceData?.prod_config || [],
      activeTab: "tab-configuration",
    };
  },

  computed: {
    isCreate() {
      return this.action === QUERY_ACTIONS_ADD;
    },

    credentialsFields() {
      return this.service.fields.credentials;
    },

    additionalConfigFields() {
      return this.service.fields.additional_config;
    },

    payload() {
      const { test_config, prod_config, name, slug } = this.serviceData;

      return {
        service_name: name,
        service_type: slug,
        test_config: { ...this.test_config },
        prod_config: { ...this.prod_config },
      };
    },
  },

  created() {},

  methods: {
    async toggleServiceActivation(is_active) {
      const that = this;
      return await swal.fire({
        text: this.serviceData.is_deactivated
          ? this.$t("ELIXIO_CONNECT.ELIXIO_CONNECT_CONFIRM_THIS_REACTIVATION")
          : this.$t("ELIXIO_CONNECT.ELIXIO_CONNECT_CONFIRM_THIS_DEACTIVATION"),
        customClass: {
          popup: this.serviceData.is_deactivated
            ? "warning-confirm"
            : "delete-popup",
        },
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: that.$t("COMMON.YES"),
        cancelButtonText: that.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
        showLoaderOnConfirm: that.loading,
        allowOutsideClick: () => !swal.isLoading(),

        preConfirm: async () => {
          this.loading = true;
          let success = false;

          try {
            const formData = {
              organizationId: this.organization.id,
              payload: {
                service_name: this.serviceData.name,
                service_type: this.serviceData.slug,
                is_active: is_active,
              },
            };

            console.clear();

            const response = await this.$store.dispatch(
              "elixioConnectModule/toggleServiceActivation",
              formData
            );

            if (response.status === 200) {
              this.$notify({
                type: "success",
                message: this.$t("ELIXIO_CONNECT.OPERATION_SUCCEEDED"),
              });

              success = true;
              this.serviceData.is_active = false;
            }
          } catch (error) {
            console.error("toggleServiceActivation => ", error);

            swal.showValidationMessage(`Erreur: ${error}`); // Gère les erreurs

            this.$notify({
              type: "danger",
              message: this.$t("ELIXIO_CONNECT.SOMETHING_WENT_WRONG"),
            });
          } finally {
            this.loading = false;
            this.$emit("onSubmited", { success, service: this.serviceData });
          }
        },
      });
    },

    async handleSubmit() {
      const that = this;
      return await swal.fire({
        text: this.$t("ELIXIO_CONNECT.ELIXIO_CONNECT_CONFIRM_THIS_SUBSCRIPTION"),
        customClass: {
          popup: "warning-confirm",
        },
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: that.$t("COMMON.YES"),
        cancelButtonText: that.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
        showLoaderOnConfirm: that.loading,
        allowOutsideClick: () => !swal.isLoading(),

        preConfirm: async () => {
          if (!that.serviceData.is_active && !that.serviceData.is_deactivated) {
            await that.subscribe();
          } else {
            await that.submitConfig();
          }
        },
      });
    },

    async submitConfig() {
      this.loading = true;
      let success = false;

      const organizationId = this.organization.id;
      const service = this.serviceData;

      const payload = {
        additional_config: { ...service.additional_config },
        service_name: service.name,
        service_type: service.slug,
        test_config: this.payload.test_config,
        prod_config: this.payload.prod_config,
      };

      try {
        const formData = { organizationId, payload };

        console.clear();

        const response = await this.$store.dispatch(
          "elixioConnectModule/updateConfigs",
          formData
        );

        if (response.status === 200) {
          this.$notify({
            type: "success",
            message: response.data?.message ?? "Réussi",
          });

          success = true;
        }
      } catch (error) {
        console.error("submitConfig => ", error);

        swal.showValidationMessage(`Erreur: ${error}`); // Gère les erreurs

        this.$notify({
          type: "danger",
          message: this.$t("ELIXIO_CONNECT.SOMETHING_WENT_WRONG"),
        });

        // this.formErrors = error.response.data.errors;
      } finally {
        this.loading = false;
        this.$emit("onSubmited", { success, service: this.serviceData });
      }
    },

    async subscribe() {
      this.loading = true;
      let success = false;

      try {
        const formData = {
          organizationId: this.organization.id,
          payload: {
            service_name: this.serviceData.name,
            service_type: this.serviceData.slug,
            test_config: this.payload.test_config,
            prod_config: this.payload.prod_config,
          },
        };

        console.clear();

        const response = await this.$store.dispatch(
          "elixioConnectModule/subscribe",
          formData
        );

        if (response.status !== 200) {
          return;
        }
        // this.subscribed = true;

        if ("service_data" in response.data) {
          const { data } = response;

          const serviceId = data.service_data.elixio_service_id ?? null;
          this.serviceData.id = serviceId;

          success = true;

          if (serviceId) {
            swal.close();
            console.log("serviceData => ", this.serviceData);
            // La fenêtre est fermée, on déclenche le refresh
            this.handleFormSubmited({ success: true, service: this.serviceData });

            this.$notify({
              type: "success",
              message:this.$t("ELIXIO_CONNECT.AUTH_FINISHED"),
            });
          } else {
            this.$notify({
              type: "warning",
              message: this.$t("ELIXIO_CONNECT.NOT_CONFIGURED_YET"),
            });
          }
        }
      } catch (error) {
        console.error("Subscribe => ", error);

        swal.showValidationMessage(`Erreur: ${error}`); // Gère les erreurs

        this.$notify({
          type: "danger",
          message: this.$t("ELIXIO_CONNECT.SOMETHING_WENT_WRONG"),
        });
      } finally {
        this.loading = false;
      }
    },

    async authorize() {
      return await swal.fire({
        title: this.$t("ELIXIO_CONNECT.ELIXIO_CONNECT_CONFIRM_THIS_SUBSCRIPTION"),
        icon: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
        showLoaderOnConfirm: this.loading,
        allowOutsideClick: () => !swal.isLoading(), // Empêche la fermeture accidentelle pendant le chargement

        preConfirm: async () => {
          return await this.subscribe();
        },
      });
    },

    close(refresh = false) {
      this.$emit("onCancelled");
    },

    handleFormSubmited({ success, service }) {
      this.$emit("onSubmited", { success, service });
    },
  },

  mounted() {},

  watch: {
    service(newValue) {
      this.serviceData = cloneDeep(newValue ?? emptyService);
    },
  },
};
</script>

<style lang="scss">
.elite-modal-forms {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  button {
    min-height: 48px;
    border-radius: 8px;
    padding: 0 40px;
    margin: 0 16px 0 0;
    float: none;
    &.btn {
      opacity: 1;
      &:not(:last-child) {
        margin-right: 16px;
      }
      &:last-child {
        margin: 0;
      }
      &.edit {
        background-color: var(--primary);
        color: #fff;
        -webkit-transition: all 0.3s ease-in-out;
        -moz-transition: all 0.3s ease-in-out;
        transition: all 0.3s ease-in-out;
        &:hover {
          background-color: var(--darken-primary);
        }
      }
      &.close {
        background-color: #9ca3af;
        color: #fff;
      }
    }
  }
}
</style>
